import ServiceWrapper, { type ServiceOptions } from "@utils/ServiceWrapper";
import {
    STUDENT_INFO_BUS_ROUTE,
    STUDENT_INFO_CUSTOM_ATTRIBUTES,
    STUDENT_INFO_HOME_SCHOOL,
    STUDENT_INFO_STUDENT,
    STUDENTS_BY_GUARDIAN_GET
} from "@const/StudentInfoConst";

type NullableKey = number | string | null;

export type StudentInfoAction =
    | "customAttributeRead"
    | "customAttributesRead"
    | "studentsByGuardianRead"
    | "studentBusRoutesRead"
    | "studentImageRead"
    | "studentInfoStudentRead";

export interface StudentInfoDaoOptions {
    action: StudentInfoAction;
    attributeName?: string | null;
    ignoreError?: boolean;
    objectName?: string | null;
    params?: unknown;
    personId?: NullableKey;
    retrieveAllFlag?: boolean;
    schoolId?: NullableKey;
    schoolYearKey?: NullableKey;
    studentNumber?: NullableKey;
    username?: string | null;
    signal?: AbortSignal;
}

/**
 * Data Access Object for the Student Info API.
 */
const StudentInfoDao = <T = unknown>(options: StudentInfoDaoOptions): Promise<{ data: T }> | null => {
    const {
        action,
        attributeName,
        objectName,
        params,
        personId,
        schoolId,
        schoolYearKey,
        studentNumber,
        username,
        signal
    } = options;

    const requestOptions: ServiceOptions = { url: "", method: "GET" };

    const actionConfig: Record<string, Partial<ServiceOptions>> = {
        customAttributeRead: {
            method: "GET",
            params,
            url: `${STUDENT_INFO_CUSTOM_ATTRIBUTES}/${studentNumber}/${attributeName}/${objectName}/detail.json`
        },
        customAttributesRead: {
            method: "GET",
            params: params ?? undefined,
            url: `${STUDENT_INFO_CUSTOM_ATTRIBUTES}/${studentNumber}/index.json`
        },
        studentsByGuardianRead: { method: "GET", params, url: `${STUDENTS_BY_GUARDIAN_GET}/${username}/index.json` },
        studentBusRoutesRead: { method: "GET", url: `${STUDENT_INFO_BUS_ROUTE}/${studentNumber}/detail.json` },
        studentImageRead: {
            method: "GET",
            responseType: "blob",
            url: `${STUDENT_INFO_STUDENT}/${studentNumber}/${schoolYearKey}/image`
        },
        studentInfoStudentRead: { method: "GET", url: `${STUDENT_INFO_HOME_SCHOOL}/${personId}/${schoolId}/check.json` }
    };

    const config = actionConfig[action];
    if (!config) {
        const error = new Error(`StudentInfoDao: Invalid action "${action}" provided`);
        console.error("StudentInfoDao: Invalid action provided");
        throw error;
    }

    return ServiceWrapper.serviceCall({
        options: { ...requestOptions, ...config, signal }
    }) as Promise<{ data: T }>;
};

export default StudentInfoDao;
